import { Handshake, SatelliteAlt, AppSettingsAlt, Call } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { CgExtensionAdd } from 'react-icons/cg';
import { FaRegShareSquare } from 'react-icons/fa';
import { MdOutlineScreenSearchDesktop } from 'react-icons/md';
import { useLocation } from 'react-router-dom';

import { useWorkspaceContext } from '../../../context';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { ClientAction, useClientAuthz } from '../../../hooks/useClientAuthz';
import {
  useJobBoardAccessControl,
  PremiumJobBoardsAction,
} from '../../../hooks/useJobBoardAccessControl';
import {
  useNativeSearchAccessControl,
  NativeSearchAction,
} from '../../../hooks/useNativeSearchAccessControl';
import {
  SmsConfigAction,
  useSmsConfigAccessControl,
} from '../../../hooks/useSmsConfigAccessControl';
import { UserAccessAction, useUserAccessAuthz } from '../../../hooks/useUserAccessAuthz';
import { VmsConfigAction, useVmsAccessControl } from '../../../hooks/useVmsAccessControl';
import AppSettingsLayout from '../AppSettingsLayout';
import SettingsContentLayout from '../components/SettingsContentLayout';
import {
  accountOptions,
  analyticsOptions,
  appsAndIntegrationsOptions,
  careerPagesOptions,
  crmOptions,
  crmOptionsWithEvaluation,
  crmManageRecruiterActions,
  crmStageStatus,
  crmManageCandidateResponse,
  crmCandidateDownload,
  crmPaginationSettings,
  dataPrivacyOptions,
  helpAndKnowledgeOptions,
  jobsOptions,
  jobsOptionsFields,
  jobsDataDownload,
  jobsAttributeVisibility,
  defineJobClosing,
  configureJobPosting,
  teamsAndUsersOptions,
  teamsAndOptions,
  invoiceModuleOptions,
  clientModuleOptions,
  placementModuleOptions,
  workspaceSettingOptions,
  crmCustomizeSubmissionFlow,
  crmCustomizeMappingForm,
  organizationalUnitOption,
  automationOptions,
  // crusibleCrmOptions,
} from '../options/Options';
import {
  accountOptionsForUser,
  appsAndIntegrationsOptionsForUser,
  appsAndIntegrationsOptionsForCrusibleUser,
  faqOptionsForUser,
  teamsAndUsersOptionsAccess,
  teamsAndUsersOptionsForUser,
} from '../options/UserOptions';
import { ISettingsMenuOption, SettingsMenuItems } from '../types';

const SettingsOptions = () => {
  const isAuthorizedToAddClient = useClientAuthz(ClientAction.ADD_CLIENT_UNIT);
  const isAuthorizedToAddClientContact = useClientAuthz(ClientAction.ADD_CLIENT_CONTACT_UNIT);

  const admin = useWorkspaceContext();
  const { access: nativeSearchAccess } = useNativeSearchAccessControl([
    NativeSearchAction.VIEW_NATIVE_SEARCH_ACCOUNTS,
    NativeSearchAction.MANAGE_NATIVE_SEARCH_ACCOUNTS,
    NativeSearchAction.DELETE_JOB_BOARD_ACCOUNTS,
  ]);
  const { access: jobBoardAccess } = useJobBoardAccessControl([
    PremiumJobBoardsAction.VIEW_PREMIUM_JOB_POSTING_ACCOUNTS,
    PremiumJobBoardsAction.MANAGE_PREMIUM_JOB_POSTING_CREDITS,
    PremiumJobBoardsAction.DELETE_PREMIUM_JOB_POSTING_ACCOUNTS,
  ]);
  const { access: smsConfigAccess } = useSmsConfigAccessControl([
    SmsConfigAction.VIEW_SMS_CONFIGURATION,
    SmsConfigAction.MANAGE_SMS_CONFIGURATION,
    SmsConfigAction.MANAGE_SMS_LOGS,
    SmsConfigAction.MANAGE_SMS_USERS,
    SmsConfigAction.DELETE_SMS_CONFIGURATION,
  ]);
  const { access: vmsConfigAccess } = useVmsAccessControl([
    VmsConfigAction.VIEW_VMS_CONFIGURATION,
    VmsConfigAction.EDIT_VMS_CONFIGURATION,
    VmsConfigAction.DELETE_VMS_CONFIGURATION,
  ]);

  const { access: userAccessControl } = useUserAccessAuthz([
    UserAccessAction.VIEW_USERS_ACCESS,
    UserAccessAction.MANAGE_USERS_ACCESS,
  ]);

  const { access: jobsAtsControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.JOB_FIELDS_CUSTOMISATION,
    AtsCustomAPIAccess.REASON_FOR_CLOSING_JOB_SETTING,
    AtsCustomAPIAccess.JOB_ADMIN_TEAM_AS_MANDATORY,
    AtsCustomAPIAccess.DATA_DOWNLOAD_SETTINGS_IN_JOBS,
    AtsCustomAPIAccess.JOB_ATTRIBUTE_VISIBILITY_SETTINGS,
  ]);

  const { access: crmAtsControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MAPPING_FIELDS_CUSTOMISATION,
    AtsCustomAPIAccess.SUBMISSION_FIELDS_CUSTOMISATION,
    AtsCustomAPIAccess.RECRUITER_ACTIONS,
    AtsCustomAPIAccess.CANDIDATE_RESPONSES,
    AtsCustomAPIAccess.STAGE_STATUS_CHANGES,
    AtsCustomAPIAccess.DATA_DOWNLOAD_SETTINGS_IN_CANDIDATES,
    AtsCustomAPIAccess.PAGINATION_SETTINGS,
  ]);

  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.CAREER_PAGE_SETTINGS,
    AtsCustomAPIAccess.CLIENT_SETTINGS,
    AtsCustomAPIAccess.INVOICE_SETTINGS,
    AtsCustomAPIAccess.PRISM_SETTINGS,
    AtsCustomAPIAccess.DATA_SHARING_SETTINGS,
    AtsCustomAPIAccess.CALL_SETTINGS,
    AtsCustomAPIAccess.PROMPT_FOR_CHATGPT,
  ]);

  const jobAcccess =
    jobsAtsControl[0] ||
    jobsAtsControl[1] ||
    jobsAtsControl[2] ||
    jobsAtsControl[3] ||
    jobsAtsControl[4];

  const crmAcccess =
    crmAtsControl[0] ||
    crmAtsControl[1] ||
    crmAtsControl[2] ||
    crmAtsControl[3] ||
    crmAtsControl[4] ||
    crmAtsControl[5] ||
    crmAtsControl[6];

  const viewAccess = admin.owns || userAccessControl[0] || userAccessControl[1];
  const workspaceContext = useWorkspaceContext();
  const workspaceIds = new Set([
    '0ed56aea-54c9-4485-987f-ffb35872eb38',
    'd50db869-8d00-430a-b5e7-a379dbc50a7d',
    '74b36033-196e-4a9f-8c0a-638190fb7e74',
  ]);

  const location = useLocation();
  const [options, setOptions] = useState<ISettingsMenuOption[]>();
  const [filteredCrmOptions, setFilteredCrmOptions] = useState<ISettingsMenuOption[]>();
  const [filteredJobOptions, setFilteredJobOptions] = useState<ISettingsMenuOption[]>();
  const [title, setTitle] = useState<string>('');

  useEffect(() => {
    const filteredOptions = getFilteredOption();
    setFilteredCrmOptions(filteredOptions);

    const finalOptions: ISettingsMenuOption[] = [];
    if (jobsAtsControl[0]) {
      finalOptions.push(jobsOptionsFields);
    }
    if (jobsAtsControl[1]) {
      finalOptions.push(defineJobClosing);
    }
    if (jobsAtsControl[2]) {
      finalOptions.push(configureJobPosting);
    }
    if (jobsAtsControl[3]) {
      finalOptions.push(jobsDataDownload);
    }
    if (jobsAtsControl[4]) {
      finalOptions.push(jobsAttributeVisibility);
    }
    setFilteredJobOptions(finalOptions);
  }, []);

  const getFilteredOption = () => {
    const finalOptions: ISettingsMenuOption[] = [];
    if (crmAtsControl[0] || crmAtsControl[1]) {
      finalOptions.push(crmCustomizeMappingForm);
    }
    if (crmAtsControl[1]) {
      finalOptions.push(crmCustomizeSubmissionFlow);
    }

    if (crmAtsControl[2]) {
      finalOptions.push(crmManageRecruiterActions);
    }
    if (crmAtsControl[3]) {
      finalOptions.push(crmManageCandidateResponse);
    }
    if (crmAtsControl[4]) {
      finalOptions.push(crmStageStatus);
    }
    if (crmAtsControl[5]) {
      finalOptions.push(crmCandidateDownload);
    }
    if (crmAtsControl[6]) {
      finalOptions.push(crmPaginationSettings);
    }
    return finalOptions;
  };

  useEffect(() => {
    const hasAccessToOption = {
      NATIVE_SEARCH:
        admin.owns || nativeSearchAccess[0] || nativeSearchAccess[1] || nativeSearchAccess[2],
      JOB_BOARDS: admin.owns || jobBoardAccess[0] || jobBoardAccess[1] || jobBoardAccess[2],
      SMS_CONFIGURATION:
        admin.owns ||
        smsConfigAccess[0] ||
        smsConfigAccess[1] ||
        smsConfigAccess[2] ||
        smsConfigAccess[3] ||
        smsConfigAccess[4],
      VMS_CONFIGURATION:
        admin.owns || vmsConfigAccess[0] || vmsConfigAccess[1] || vmsConfigAccess[2],
      PRISM_CONFIGURATION: admin.owns || atsCustomAccessControl[3],
      DATA_SHARING: admin.owns || atsCustomAccessControl[4],
      CALL_SETTINGS: !admin.owns || atsCustomAccessControl[5],
      PROMPT_FOR_CHATGPT: admin.owns || atsCustomAccessControl[6],
    };

    const modifyOptionsWithAccess = {
      NATIVE_SEARCH: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'Native Search').length === 0) {
          options.splice(1, 0, {
            label: 'Native Search',
            url: '/native-search-settings/',
            icon: <MdOutlineScreenSearchDesktop />,
            SKU: '',
          });
          return options;
        }
        return options;
      },
      JOB_BOARDS: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'External Job Posting').length === 0) {
          options.splice(0, 0, {
            label: 'External Job Posting',
            url: '/job-boards',
            icon: <CgExtensionAdd />,
            SKU: '',
          });
          return options;
        }
        return options;
      },
      SMS_CONFIGURATION: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'Communication Accounts').length === 0) {
          options.splice(2, 0, {
            label: 'Communication Accounts',
            url: '/sms-configuration',
            icon: <SatelliteAlt />,
            SKU: '',
          });
          return options;
        }
        return options;
      },
      VMS_CONFIGURATION: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'Vendor Management System').length === 0) {
          options.splice(12, 0, {
            label: 'Vendor Management System',
            url: '/vms',
            icon: <Handshake />,
            SKU: '',
          });
          return options;
        }
        return options;
      },
      PRISM_CONFIGURATION: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'Prism Settings').length === 0) {
          options.splice(9, 0, {
            label: 'Prism Settings',
            url: '/prism-extension-settings/',
            icon: <AppSettingsAlt />,
            SKU: '',
          });
          return options;
        }
        return options;
      },

      PROMPT_FOR_CHATGPT: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'Prompt Settings').length === 0) {
          options.splice(9, 0, {
            label: 'Prompt Settings',
            url: '/prompt-settings/',
            icon: <AppSettingsAlt />,
            SKU: '',
          });
          return options;
        }
        return options;
      },

      DATA_SHARING: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'Data Sharing').length === 0) {
          options.splice(2, 0, {
            label: 'Data Sharing',
            url: '/data-sharing',
            icon: <FaRegShareSquare />,
            SKU: '',
          });
          return options;
        }
        return options;
      },
      CALL_SETTINGS: (options: ISettingsMenuOption[]) => {
        if (options.filter((option) => option.label === 'Call Settings').length === 0) {
          options.splice(6, 0, { label: 'Call Settings', url: './', icon: <Call />, SKU: '' });
          return options;
        }
        return options;
      },
    };
    switch (location.pathname.split('/')[2]) {
      case SettingsMenuItems.TEAMS_AND_USERS: {
        if (admin.owns) {
          viewAccess
            ? setOptions(teamsAndUsersOptions)
            : isAuthorizedToAddClientContact || isAuthorizedToAddClient
            ? setOptions([...teamsAndOptions, organizationalUnitOption])
            : setOptions(teamsAndOptions);
        } else {
          const options = (
            viewAccess ? teamsAndUsersOptionsAccess : teamsAndUsersOptionsForUser
          ) as ISettingsMenuOption[];
          if (hasAccessToOption.DATA_SHARING) {
            modifyOptionsWithAccess.DATA_SHARING(options);
          }
          isAuthorizedToAddClientContact || isAuthorizedToAddClient
            ? setOptions([...options, organizationalUnitOption])
            : setOptions(options);
        }
        setTitle('Teams And Users');
        break;
      }
      case SettingsMenuItems.JOBS: {
        if (admin.owns && !jobAcccess) {
          setOptions(jobsOptions);
        } else if (jobAcccess) {
          setOptions(filteredJobOptions);
        } else {
          setOptions([]);
        }
        setTitle('Jobs');
        break;
      }
      case SettingsMenuItems.ANALYTICS: {
        admin.owns ? setOptions(analyticsOptions) : setOptions([]);
        setTitle('Analytics');
        break;
      }
      case SettingsMenuItems.CRM: {
        if (admin.owns && !crmAcccess) {
          // setOptions(crmOptions);
          if (
            workspaceContext.id === 'c982f84c-d9a8-492a-906b-34a2b1831c3e' ||
            workspaceContext.id === '17953198-992b-4ea2-85bb-f7eb04d78b14' ||
            workspaceContext.id === 'c809aa49-c696-4ea1-8467-0108a16ab786'
          ) {
            setOptions(crmOptionsWithEvaluation);
          }
          // else if (
          //   workspaceContext.id === '0ed56aea-54c9-4485-987f-ffb35872eb38' ||
          //   workspaceContext.id === '352f4535-62a5-4d74-8019-cb2ea76d8cb5' ||
          //   workspaceContext.id == '963fd174-fdc9-4527-9e0f-f541925457c6'
          // ) {
          //   setOptions(crusibleCrmOptions);
          // }
          else {
            setOptions(crmOptions);
          }
        } else if (crmAcccess) {
          setOptions(filteredCrmOptions);
        } else {
          setOptions([]);
        }
        setTitle('CRM');
        break;
      }
      case SettingsMenuItems.ACCOUNT: {
        admin.owns ? setOptions(accountOptions) : setOptions(accountOptionsForUser);
        setTitle('Account');
        break;
      }
      case SettingsMenuItems.DATA_PRIVACY: {
        admin.owns ? setOptions(dataPrivacyOptions) : setOptions([]);
        setTitle('Data Privacy');
        break;
      }
      case SettingsMenuItems.APPS_AND_INTEGRATIONS: {
        let options = (
          !admin.owns
            ? appsAndIntegrationsOptions
            : workspaceIds.has(workspaceContext?.id ?? '')
            ? appsAndIntegrationsOptionsForCrusibleUser
            : appsAndIntegrationsOptionsForUser
        ) as ISettingsMenuOption[];
        if (hasAccessToOption.NATIVE_SEARCH)
          options = modifyOptionsWithAccess.NATIVE_SEARCH(options);
        if (!hasAccessToOption.NATIVE_SEARCH)
          options.filter((option) => option.label !== 'Native Search');

        if (hasAccessToOption.JOB_BOARDS) options = modifyOptionsWithAccess.JOB_BOARDS(options);
        if (!hasAccessToOption.JOB_BOARDS)
          options.filter((option) => option.label !== 'External Job Posting');
        if (hasAccessToOption.SMS_CONFIGURATION)
          options = modifyOptionsWithAccess.SMS_CONFIGURATION(options);
        if (!hasAccessToOption.SMS_CONFIGURATION)
          options.filter((option) => option.label !== 'Communication Accounts');
        if (hasAccessToOption.VMS_CONFIGURATION)
          options = modifyOptionsWithAccess.VMS_CONFIGURATION(options);
        if (!hasAccessToOption.VMS_CONFIGURATION)
          options.filter((option) => option.label !== 'Vendor Management System');

        if (hasAccessToOption.PRISM_CONFIGURATION) {
          modifyOptionsWithAccess.PRISM_CONFIGURATION(options);
        }
        if (hasAccessToOption.PROMPT_FOR_CHATGPT) {
          modifyOptionsWithAccess.PROMPT_FOR_CHATGPT(options);
        }

        if (hasAccessToOption.CALL_SETTINGS) {
          modifyOptionsWithAccess.CALL_SETTINGS(options);
        }
        setOptions(options);

        setTitle('Apps And Integrations');
        break;
      }
      case SettingsMenuItems.CAREER_PAGES: {
        admin.owns || atsCustomAccessControl[0] ? setOptions(careerPagesOptions) : setOptions([]);
        setTitle('Career Pages');
        break;
      }
      case SettingsMenuItems.INVOICE_MODULE: {
        admin.owns || atsCustomAccessControl[2] ? setOptions(invoiceModuleOptions) : setOptions([]);
        setTitle('Invoice Module');
        break;
      }
      case SettingsMenuItems.CLIENTS: {
        admin.owns || atsCustomAccessControl[1] ? setOptions(clientModuleOptions) : setOptions([]);
        setTitle('Clients');
        break;
      }
      case SettingsMenuItems.PLACEMENT: {
        admin.owns ? setOptions(placementModuleOptions) : setOptions([]);
        setTitle('Placement');
        break;
      }
      case SettingsMenuItems.HELP_AND_KNOWLEDGE: {
        admin.owns ? setOptions(helpAndKnowledgeOptions) : setOptions([]);
        setTitle('Help And Knowledge');
        break;
      }
      case SettingsMenuItems.AUTOMATION: {
        admin.owns ? setOptions(automationOptions) : setOptions([]);
        setTitle('Automation');
        break;
      }
      case SettingsMenuItems.FAQS: {
        admin.owns ? setOptions([]) : setOptions(faqOptionsForUser);
        setTitle('FAQs');
        break;
      }
      case SettingsMenuItems.WORKSPACE_SETTINGS: {
        admin.owns ? setOptions(workspaceSettingOptions) : setOptions([]);
        setTitle('Workspace Settings');
        break;
      }

      default: {
        admin.owns ? setOptions(teamsAndUsersOptions) : setOptions([]);
        setTitle('Teams And Users');
        break;
      }
    }
  }, [
    admin.owns,
    atsCustomAccessControl,
    crmAcccess,
    crmAtsControl,
    filteredCrmOptions,
    filteredJobOptions,
    jobAcccess,
    jobBoardAccess,
    location.pathname,
    nativeSearchAccess,
    smsConfigAccess,
    viewAccess,
    vmsConfigAccess,
  ]);

  return (
    <AppSettingsLayout title={title} backUrl="/home">
      <SettingsContentLayout options={options || []} />
    </AppSettingsLayout>
  );
};

export default SettingsOptions;
